import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts/highstock";
import { ChartSeriesData } from "../common";
import { Line } from "./types";
import { getAnnotationShapes } from "./utils";

export const useDrawing = (
  allowDrawing: boolean,
  chartData: ChartSeriesData[],
  drawableLinesColor: string[],
  chart?: Highcharts.Chart
) => {
  const [chartLines, setChartLines] = useState<Line[]>([]);
  const [tempLine, setTempLine] = useState<Line>();
  const tempCoordinates = useRef<Line>(null);
  const isHoldingClick = useRef(false);

  const annotations = useMemo(() => {
    const lines =
      isHoldingClick.current && tempLine
        ? [...chartLines, tempLine]
        : chartLines;
    return getAnnotationShapes(lines, drawableLinesColor);
  }, [drawableLinesColor, chartLines, tempLine]);

  const mouseDown = useCallback(
    (event: MouseEvent, chart?: Highcharts.Chart) => {
      if (!chart) return;
      const { xAxis, yAxis } = chart;
      const mouseCoords = chart.pointer.normalize(event);
      const xValue = xAxis[0].toValue(mouseCoords.chartX);
      const yValue = yAxis[0].toValue(mouseCoords.chartY);
      const newLine = {
        x1: xValue,
        y1: yValue,
        x2: 0,
        y2: 0,
      };
      tempCoordinates.current = newLine;
      isHoldingClick.current = true;
    },
    []
  );

  const mouseMove = useCallback(
    (event: MouseEvent, chart?: Highcharts.Chart) => {
      if (!tempCoordinates.current || !isHoldingClick.current || !chart) return;
      const { xAxis, yAxis } = chart;
      const mouseCoords = chart.pointer.normalize(event);
      const xValue = xAxis[0].toValue(mouseCoords.chartX);
      const yValue = yAxis[0].toValue(mouseCoords.chartY);

      if (tempCoordinates.current) {
        setTempLine({
          ...tempCoordinates.current,
          x2: xValue,
          y2: yValue,
        });
      }
    },
    []
  );

  const mouseUp = useCallback((event: MouseEvent, chart?: Highcharts.Chart) => {
    if (!chart) return;
    const { xAxis, yAxis } = chart;
    const mouseCoords = chart.pointer.normalize(event);
    const xValue = xAxis[0].toValue(mouseCoords.chartX);
    const yValue = yAxis[0].toValue(mouseCoords.chartY);
    if (tempCoordinates.current) {
      setChartLines((cc) => {
        const newCC = [
          ...cc,
          {
            ...(tempCoordinates.current ?? { x1: 0, y1: 0 }),
            x2: xValue,
            y2: yValue,
          },
        ];

        return newCC;
      });
      isHoldingClick.current = false;
      setTempLine(undefined);
    }
  }, []);

  useEffect(() => {
    const container = chart?.container;
    const mouseDownHandler = (event: MouseEvent) => mouseDown(event, chart);
    const mouseUpHandler = (event: MouseEvent) => mouseUp(event, chart);
    const mouseMoveHandler = (event: MouseEvent) => mouseMove(event, chart);
    if (!allowDrawing || !chartData || !chart) {
      //clear everything once the drawing is
      setChartLines([]);
      isHoldingClick.current = false;
      setTempLine(undefined);
      container?.removeEventListener("mousedown", mouseDownHandler);
      container?.removeEventListener("mouseup", mouseUpHandler);
      container?.removeEventListener("mousemove", mouseMoveHandler);
      return;
    }
    container?.addEventListener("mousedown", mouseDownHandler);
    container?.addEventListener("mouseup", mouseUpHandler);
    container?.addEventListener("mousemove", mouseMoveHandler);

    return () => {
      container?.removeEventListener("mousedown", mouseDownHandler);
      container?.removeEventListener("mouseup", mouseUpHandler);
      container?.removeEventListener("mousemove", mouseMoveHandler);
    };
  }, [allowDrawing, mouseDown, mouseMove, mouseUp, chartData, chart]);

  return { annotations, isDrawing: isHoldingClick };
};
